import {
  Component,
  forwardRef,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ValidatorFn,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { merge, Subject, takeUntil } from 'rxjs';
import {
  CountryCode,
  formatIncompletePhoneNumber,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { PayeeInputDirective } from '../../payee-input/payee-input.directive';
import { MatIcon } from '@angular/material/icon';
import { CountryFlagPipe } from '../../pipes/country-flag/country-flag.pipe';

@Component({
  selector: 'payee-phone',
  templateUrl: './payee-phone.component.html',
  styleUrls: ['./payee-phone.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PayeePhoneComponent),
      multi: true,
    },
  ],
  imports: [
    PayeeInputDirective,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    CountryFlagPipe,
    TranslateModule,
  ],
})
export class PayeePhoneComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  private translate = inject(TranslateService);

  private readonly destroy = new Subject<void>();
  readonly areaCode = new FormControl<CountryCode>('HU');
  readonly numberValue = new FormControl<string | null>(
    '',
    this.phoneNumberValidator(this.areaCode),
  );
  countries = getCountries();
  readonly getCountryCallingCode = getCountryCallingCode;

  readonly countryPickerOpen = signal(false);
  countrySearch = '';
  isDisabled = false;

  get countrySearchPlaceholder(): string {
    const country = this.areaCode.value as CountryCode;
    return `+${getCountryCallingCode(country)} / ${this.getCountryDisplayName(country)} / ${country}`;
  }

  ngOnInit(): void {
    merge(this.areaCode.valueChanges, this.numberValue.valueChanges)
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        const changed = this.formatNumberValue();
        if (changed) {
          return;
        }

        if (!this.areaCode.value || !this.numberValue.value) {
          this.onChange('');
        } else {
          this.onChange(
            `+${getCountryCallingCode(this.areaCode.value)}${this.numberValue.value}`,
          );
        }
      });

    this.areaCode.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.numberValue.updateValueAndValidity());
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  getCountryDisplayName(iso: CountryCode): string {
    const regionNames = new Intl.DisplayNames(this.translate.currentLang, {
      type: 'region',
    });
    return regionNames.of(iso) || 'N/A';
  }

  toggleCountryPicker(event: Event): void {
    event.stopPropagation();
    if (this.isDisabled) {
      return;
    }

    this.countryPickerOpen.update((open) => !open);
    if (this.countryPickerOpen()) {
      this.resetCountryFilter();
    }
  }

  selectCountry(country: CountryCode): void {
    this.areaCode.setValue(country);
    this.closeCountryPicker();
    this.onTouched();
  }

  onCountrySearchInput(value: string): void {
    this.countrySearch = value;
    this.filterAreaCodes(value);
  }

  closeCountryPicker(): void {
    this.countryPickerOpen.set(false);
    this.countrySearch = '';
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeCountryPicker();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeCountryPicker();
  }

  filterAreaCodes(filter: string): void {
    if (!filter) {
      this.resetCountryFilter();
      return;
    }

    this.countries = getCountries().filter(
      (v) =>
        v === this.areaCode.value ||
        v.toLowerCase().includes(filter.toLowerCase()) ||
        `+${getCountryCallingCode(v)}`.includes(filter) ||
        this.getCountryDisplayName(v)
          .toLowerCase()
          .includes(filter.toLowerCase()),
    );
  }

  onChange = (_obj: any) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    if (typeof obj !== 'string') {
      this.areaCode.patchValue('HU');
      this.numberValue.patchValue(null);
      return;
    }

    const phoneNumber = parsePhoneNumberFromString(obj, 'HU');
    if (phoneNumber) {
      this.areaCode.patchValue(phoneNumber.country || 'HU', {
        emitEvent: false,
      });
      this.numberValue.patchValue(phoneNumber.nationalNumber, {
        emitEvent: false,
      });
    } else {
      this.areaCode.patchValue('HU');
      this.numberValue.patchValue(obj, { emitEvent: false });
    }
    this.formatNumberValue();
  }

  registerOnChange(fn: (obj: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.areaCode.disable();
      this.numberValue.disable();
      this.closeCountryPicker();
    } else {
      this.areaCode.enable();
      this.numberValue.enable();
    }
  }

  /**
   * @returns {boolean} value changed
   */
  private formatNumberValue(): boolean {
    const value = this.numberValue.value;
    if (typeof value !== 'string') {
      return false;
    }

    const formatted = formatIncompletePhoneNumber(
      value,
      this.areaCode.value || 'HU',
    );
    if (formatted !== value) {
      this.numberValue.patchValue(formatted);
      return true;
    }

    return false;
  }

  private resetCountryFilter(): void {
    this.countrySearch = '';
    this.countries = getCountries();
  }

  private phoneNumberValidator(areaCode: AbstractControl): ValidatorFn {
    return (control) => {
      if (!control.value) {
        return null;
      }

      const phoneNumber = parsePhoneNumberFromString(
        control.value,
        areaCode.value || 'HU',
      );
      if (phoneNumber?.isValid()) {
        return null;
      }

      return {
        phoneNumber:
          getExampleNumber(areaCode.value || 'HU', examples)?.nationalNumber ||
          'N/A',
      };
    };
  }
}
